import { useContext, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import DashboardLayout from './components/Common/DashboardLayout';
import { LAST_USED_ACCOUNT_STORAGE_KEY } from './constants/storage';
import { useAnalytics } from './contexts/AnalyticsContext';
import { AuthContext } from './contexts/AuthContext';
import { DashboardContextProvider } from './contexts/DashboardContext';
import { DynamicPageContextProvider } from './contexts/DynamicPageContext';
import { OverviewContextProvider } from './contexts/OverviewContext';
import ComingSoon from './screens/ComingSoon';
import DynamicPages from './screens/DynamicPages';
import { ExperimentsHub } from './screens/ExperimentsHub/ExperimentsHub';
import Forms from './screens/Forms';
import Leads from './screens/Leads';
import Login from './screens/Login';
import ManageDynamicPage from './screens/ManageDynamicPage/ManageDynamicPage';
import QuizFunnels from './screens/QuizFunnels';
import SearchCampaigns from './screens/SearchCampaigns';
import SocialCampaigns from './screens/SocialCampaigns';
import Workspaces from './screens/Workspaces';
import AdAccountsSettings from './screens/settings/AdAccountsSettings';
import BillingSettings from './screens/settings/BillingSettings';
import IntegrationsSettings from './screens/settings/IntegrationsSettings';
import SettingsWrapper from './screens/settings/SettingsWrapper';

import { AiAssistant } from '@/screens/AiAssistant.tsx';
import ProfileAndMembers from '@/screens/settings/ProfileAndMembers.tsx';
import WebsiteSettings from '@/screens/settings/WebsiteSettings.tsx';
import './App.css';
import RequireOnboardingComplete from './components/Common/RequireOnboardingComplete';
import { AISuggestionsContextProvider } from './contexts/AISuggestionsContextProvider';
import { ExportAnalyticsContextProvider } from './contexts/ExportAnalyticsContext';
import { ExportLeadsContextProvider } from './contexts/ExportLeadsContext';
import { OverviewConfigProvider } from './contexts/OverviewConfigContext';
import ManageDynamicPageAdsMap from './screens/ManageDynamicPage/ManageDynamicPageAdsMap';
import ManageDynamicPageAnalytics from './screens/ManageDynamicPage/ManageDynamicPageAnalytics';
import ManageDynamicPageDetails from './screens/ManageDynamicPage/ManageDynamicPageDetails';
import ManageDynamicPageExperiments from './screens/ManageDynamicPage/ManageDynamicPageExperiments';
import ManageDynamicPageLeads from './screens/ManageDynamicPage/ManageDynamicPageLeads';
import Onboard from './screens/Onboard';
import OverviewPage from './screens/OverviewPage';
import AiSettings from './screens/settings/AiSettings';

const ProtectedRoute = ({
  isAuthenticated,
  redirect,
  addGoTo,
}: {
  isAuthenticated: boolean | null;
  redirect?: string | (() => string);
  addGoTo?: boolean;
}) => {
  if (!isAuthenticated) {
    const params = [
      ...(window.location.pathname !== '/' && addGoTo
        ? ['?goto=', window.location.pathname]
        : []),
      window.location.search,
    ]
      .filter((a) => !!a)
      .filter((_, __, arr) => arr.length > 1)
      .join('');
    const redirectTo =
      !redirect || typeof redirect === 'string'
        ? redirect || '/login'
        : redirect();
    return <Navigate to={redirectTo + params} replace />;
  }

  return <Outlet />;
};

function App() {
  const location = useLocation();

  const { isAuthenticated, isAllowed } = useContext(AuthContext);
  const { track, trackPageView } = useAnalytics();

  useEffect(() => {
    track('web_app_startup');
  }, [track]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location, trackPageView]);

  return (
    <div className="h-screen overflow-y-auto">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route
          element={<ProtectedRoute addGoTo isAuthenticated={isAuthenticated} />}
        >
          <Route
            element={
              <ProtectedRoute
                redirect="/coming-soon"
                isAuthenticated={isAllowed}
              />
            }
          >
            <Route
              path=""
              element={
                localStorage.getItem(LAST_USED_ACCOUNT_STORAGE_KEY) ? (
                  <Navigate
                    to={`/workspace/${localStorage.getItem(LAST_USED_ACCOUNT_STORAGE_KEY)}`}
                  />
                ) : (
                  <Navigate to="/workspaces" />
                )
              }
            />
            <Route path="/workspaces" element={<Workspaces />} />
            <Route path="/onboard" element={<Onboard />} />
            <Route path="/onboard/:accountId" element={<Onboard />} />
            <Route
              path="/workspace/:slug"
              element={<RequireOnboardingComplete />}
            >
              <Route
                element={
                  <DashboardContextProvider>
                    <OverviewContextProvider>
                      <OverviewConfigProvider>
                        <AISuggestionsContextProvider>
                          <ExportAnalyticsContextProvider>
                            <ExportLeadsContextProvider>
                              <DashboardLayout />
                            </ExportLeadsContextProvider>
                          </ExportAnalyticsContextProvider>
                        </AISuggestionsContextProvider>
                      </OverviewConfigProvider>
                    </OverviewContextProvider>
                  </DashboardContextProvider>
                }
              >
                {/* Overview is now the default route */}
                <Route path="overview" element={<OverviewPage />} />
                <Route path="campaigns" element={<Navigate to="../pages" />} />
                <Route path="pages" element={<DynamicPages />} />
                <Route path="quiz-funnels" element={<QuizFunnels />} />
                <Route
                  path="pages/:dynamicPageId"
                  element={
                    <DynamicPageContextProvider>
                      <ManageDynamicPage />
                    </DynamicPageContextProvider>
                  }
                >
                  <Route
                    path="details"
                    element={<ManageDynamicPageDetails />}
                  />
                  <Route path="adMap" element={<ManageDynamicPageAdsMap />} />
                  <Route
                    path="analytics"
                    element={<ManageDynamicPageAnalytics />}
                  />
                  <Route path="leads" element={<ManageDynamicPageLeads />} />
                  <Route
                    path="experiments"
                    element={<ManageDynamicPageExperiments />}
                  />

                  <Route path="*" element={<Navigate replace to="details" />} />
                  <Route path="" element={<Navigate replace to="details" />} />
                </Route>
                <Route path="search-campaigns" element={<SearchCampaigns />} />
                {/* <Route path="aaa" element={<Agent />} /> */}
                <Route path="social-campaigns" element={<SocialCampaigns />} />
                <Route path="leads" element={<Leads />} />
                <Route path="experiments" element={<ExperimentsHub />} />
                <Route path="assistant" element={<AiAssistant />} />
                <Route path="settings" element={<SettingsWrapper />}>
                  <Route path="website" element={<WebsiteSettings />} />
                  <Route path="ai-settings" element={<AiSettings />} />
                  <Route path="forms" element={<Forms />} />
                  <Route path="ad-accounts" element={<AdAccountsSettings />} />
                  <Route
                    path="integrations"
                    element={<IntegrationsSettings />}
                  />
                  <Route path="billing" element={<BillingSettings />} />
                  <Route path="organization" element={<ProfileAndMembers />} />
                  <Route path="" element={<Navigate replace to="website" />} />
                </Route>
                <Route path="*" element={<Navigate replace to="overview" />} />
                <Route path="" element={<Navigate replace to="overview" />} />
                {/* Removed the old default redirect - empty path now goes to Overview */}
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate replace to="" />} />
      </Routes>
    </div>
  );
}

export default App;
